import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ADD_NEW, DROPDOWN_MIN_CHARS, FieldSize, Size, t, useDropdownSearch, type DropdownKey, type IconMeaning, type SearchFn, type SuggestionRow } from '@bms2/shared';
import { Icon } from '../Icon';
import { nameInputWhenCut, nameWhenCut } from './nameWhenCut';
import { FieldShell } from './FieldShell';
import { IconButton } from './IconButton';
import fieldStyles from './field.module.css';
import styles from './Dropdown.module.css';

/**
 * Dropdown / Autocomplete (piece 3): a replacement, because neither select nor
 * datalist can hold a two-line row or the mandatory Add New X footer. The
 * search runs on the server against the application's own records through
 * the injected `search`, debounced 250ms with a two-character minimum and
 * cancellation of the in-flight request; the field and the chevron are always
 * present; Enter or Tab selects, Escape closes, the arrows move the highlight.
 * The list renders in a layer above the page, never inside an ancestor's
 * scroll box; a chosen row shows its first line in the field; a page that
 * holds the selection hands its text back as `value`.
 *
 * That layer goes into the top-layer element the field is in — the open
 * `dialog` where there is one, and `document.body` otherwise. A modal dialog
 * is painted in the browser's top layer, above everything in the body, so a
 * list portalled to the body from inside a modal is drawn behind it and the
 * person sees nothing at all.
 */
export interface DropdownProps {
  label: string;
  /** What Add New creates, such as "Item": the footer reads Add New {entityLabel}. */
  entityLabel: string;
  search: SearchFn;
  onSelect: (row: SuggestionRow) => void;
  /**
   * The Add New X row's act. 🔒 Null withholds the row, only where a new one
   * made from the field could never be chosen in it, such as a credit note's
   * invoice, which is never a new Draft (decision 1704).
   */
  onAddNew: (() => void) | null;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  /** The debounce; 250ms unless a suite or the harness needs it shorter. */
  debounceMs?: number;
  /** Characters before a search fires; two by the plan, zero for a short closed list that lists everything on open. */
  minChars?: number;
  /** A query to start with, for the harness's open states. */
  initialQuery?: string;
  /** The text of the selection the page holds; the field shows it, and typing searches afresh. */
  value?: string | null;
  labelHidden?: boolean;
  /**
   * Which of the widths the box takes, Column in a line table's cell (decisions 0583, 0647). The list it opens
   * is placed from the field's own rectangle and keeps its own minimum, so a
   * Small field still opens a list wide enough to read.
   */
  size?: FieldSize;
  /** As wide as this many characters, where that length is exactly known; it wins over `size` (decision 0297). */
  chars?: number;
  /** A mark drawn before a row's first line: the view switcher's star on a starred view (decision 2171). */
  rowLead?: (row: SuggestionRow) => ReactNode;
  /**
   * An act at a row's end: the view switcher's pencil, which opens Edit View
   * (decision 2170). 🔨 Pressing it closes the list and never selects the row.
   */
  rowAction?: (row: SuggestionRow) => { label: string; meaning: IconMeaning; onAct: () => void } | null;
}

export const dropdownDocumentedStates = ['closed', 'open', 'empty', 'loading', 'selected'] as const;

const KEYS: ReadonlySet<string> = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape']);

/** A list is never narrower than this many value widths, so a narrow cell's list still reads; it keeps this far from the viewport's edge. */
const LIST_MIN_WIDTH_SHARE = 1.75;
const EDGE_GAP = 8;

interface ListPlacement {
  top: number;
  left: number;
  width: number;
  /** The open dialog the field sits in, or the body when it sits on a page. */
  layer: HTMLElement;
  /**
   * Which coordinate space `top` and `left` are in. On the page the list is
   * placed in the document and scrolls with it, so the compositor moves it and
   * no script runs; against a dialog, which is fixed to the viewport, the list
   * is fixed too. One transform, chosen by the layer it lands in.
   */
  scheme: 'document' | 'viewport';
}

export function Dropdown({ label, entityLabel, search, onSelect, onAddNew, placeholder, required, error, disabled, debounceMs, minChars, initialQuery, value, labelHidden, size = Size.Medium, chars, rowLead, rowAction }: DropdownProps) {
  const dropdown = useDropdownSearch({
    search,
    ...(debounceMs === undefined ? {} : { debounceMs }),
    ...(minChars === undefined ? {} : { minChars }),
    withAddNew: onAddNew !== null,
  });
  const listId = useId();
  const started = useRef(false);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [placement, setPlacement] = useState<ListPlacement | null>(null);
  const { setQuery, showText } = dropdown;
  const shownValue = useRef<string | null | undefined>(undefined);
  const threshold = minChars ?? DROPDOWN_MIN_CHARS;

  useEffect(() => {
    if (initialQuery && !started.current) {
      started.current = true;
      setQuery(initialQuery);
    }
  }, [initialQuery, setQuery]);

  // The selection the page holds shows in the field whenever it changes; typing replaces it and searches afresh.
  useEffect(() => {
    if (value === undefined || value === shownValue.current) return;
    shownValue.current = value;
    showText(value ?? '');
  }, [value, showText]);

  const showList = dropdown.open;
  const belowMinimum = dropdown.query.trim().length < threshold;

  // The hover name is read again when the shown text changes under a resting pointer, so a value the parent sets leaves no stale title (T3472).
  useLayoutEffect(() => {
    if (inputRef.current) nameInputWhenCut(inputRef.current);
  }, [dropdown.query]);
  // The list is placed under its field and follows it through scroll and
  // resize while open.
  //
  // On the page it is placed in the DOCUMENT, not the viewport: a phone
  // scrolls on the compositor and only tells a script about it afterwards, so
  // a viewport-placed layer is repainted a beat late and wiggles against the
  // field it belongs to. A layer placed in the document is carried by the same
  // scroll as the field, and no script runs at all.
  //
  // The visual viewport is listened to as well as the window, because an
  // on-screen keyboard moves what the person sees without moving the layout
  // viewport, and a layer placed once is then left behind.
  useLayoutEffect(() => {
    if (!showList) return;
    const place = () => {
      const el = controlRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const unit = parseFloat(getComputedStyle(el).getPropertyValue('--list-viewer-value-width')) || 0;
      const width = Math.max(r.width, unit * LIST_MIN_WIDTH_SHARE);
      const left = Math.max(EDGE_GAP, Math.min(r.left, window.innerWidth - width - EDGE_GAP));
      const dialog = el.closest('dialog');
      const scheme: 'document' | 'viewport' = dialog ? 'viewport' : 'document';
      setPlacement({
        top: scheme === 'document' ? r.bottom + window.scrollY : r.bottom,
        left: scheme === 'document' ? left + window.scrollX : left,
        width,
        layer: dialog ?? document.body,
        scheme,
      });
    };
    place();
    const viewport = window.visualViewport ?? null;
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    viewport?.addEventListener('scroll', place);
    viewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      viewport?.removeEventListener('scroll', place);
      viewport?.removeEventListener('resize', place);
    };
  }, [showList]);

  const choose = (row: SuggestionRow | typeof ADD_NEW) => {
    if (row === ADD_NEW) {
      dropdown.close();
      onAddNew?.();
      return;
    }
    dropdown.select(row);
    onSelect(row);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!KEYS.has(e.key)) return;
    const outcome = dropdown.onKey(e.key as DropdownKey);
    if (outcome.select) {
      choose(outcome.select);
      e.preventDefault();
    } else if (e.key !== 'Tab') {
      e.preventDefault();
    }
  };

  // Opening runs the search over whatever the field holds when nothing is loaded yet; below the minimum that shows the hint row.
  const openList = () => {
    if (dropdown.status === 'idle') dropdown.setQuery(dropdown.query);
    else dropdown.setOpen(true);
  };

  // The chevron focuses the field, so leaving the field is what closes a list the chevron opened.
  const onChevron = () => {
    if (dropdown.open) {
      dropdown.close();
      return;
    }
    const input = inputRef.current;
    const wasFocused = input !== null && document.activeElement === input;
    input?.focus();
    if (wasFocused) openList();
  };

  const addNewLabel = `${t('dropdown.addNew')} ${entityLabel}`;
  const minimumHint = `${t('dropdown.typeToSearch')} ${threshold}`;
  const listStyle: CSSProperties | undefined = placement ? { top: placement.top, left: placement.left, width: placement.width } : undefined;

  const list = showList ? (
    <ul
      id={listId}
      className={styles.list}
      role="listbox"
      style={listStyle}
      data-dropdown-list="true"
      data-placement={placement?.scheme ?? 'viewport'}
      onMouseDown={(e) => e.preventDefault()}
    >
      {belowMinimum ? (
        <li className={styles.state} role="presentation" data-row="minimum">
          {minimumHint}
        </li>
      ) : null}
      {dropdown.status === 'loading' ? (
        <li className={styles.state} role="presentation" data-row="loading">
          {t('dropdown.loading')}
        </li>
      ) : null}
      {dropdown.status === 'ready' && dropdown.rows.length === 0 ? (
        <li className={styles.state} role="presentation" data-row="empty">
          {t('dropdown.empty')}
        </li>
      ) : null}
      {dropdown.rows.map((row, index) => {
        const lead = rowLead?.(row) ?? null;
        const action = rowAction?.(row) ?? null;
        const lines = (
          <>
            <span className={styles.line1}>{row.line1}</span>
            {row.line2 ? <span className={styles.line2}>{row.line2}</span> : null}
          </>
        );
        return (
          <li
            key={row.id}
            className={styles.row}
            role="option"
            aria-selected={dropdown.highlight === index}
            data-row="suggestion"
            onMouseEnter={() => dropdown.setHighlight(index)}
            onClick={() => choose(row)}
          >
            {lead !== null || action !== null ? (
              <span className={styles.rowLine}>
                {lead !== null ? <span className={styles.rowLead}>{lead}</span> : null}
                <span className={styles.rowText}>{lines}</span>
                {action !== null ? (
                  <span className={styles.rowAction} data-row-action={row.id} onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      meaning={action.meaning}
                      label={action.label}
                      size={Size.Small}
                      name="dropdown-row-action"
                      onClick={() => {
                        dropdown.close();
                        action.onAct();
                      }}
                    />
                  </span>
                ) : null}
              </span>
            ) : (
              lines
            )}
          </li>
        );
      })}
      {onAddNew ? (
        <li
          className={`${styles.row} ${styles.addNew}`}
          role="option"
          aria-selected={dropdown.highlight === dropdown.rows.length}
          data-row="add-new"
          onMouseEnter={() => dropdown.setHighlight(dropdown.rows.length)}
          onClick={() => choose(ADD_NEW)}
        >
          {addNewLabel}
        </li>
      ) : null}
    </ul>
  ) : null;

  return (
    <FieldShell label={label} labelHidden={labelHidden ?? false} required={required ?? false} error={error ?? null} disabled={disabled ?? false} size={size} {...(chars ? { chars } : {})}>
      {({ inputId, messageId }) => (
        <div className={`${styles.wrap} ${fieldStyles.field}`} data-dropdown-state={dropdown.status} data-open={showList ? 'true' : 'false'} data-selected={value ? 'true' : 'false'}>
          <div className={fieldStyles.control} ref={controlRef}>
            <input
              id={inputId}
              ref={inputRef}
              className={`${fieldStyles.input} ${styles.input}`}
              type="text"
              role="combobox"
              aria-expanded={showList}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? messageId : undefined}
              value={dropdown.query}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              onChange={(e) => {
                nameWhenCut(e);
                dropdown.setQuery(e.target.value);
              }}
              onKeyDown={onKeyDown}
              onMouseEnter={nameWhenCut}
              onFocus={(event) => {
                nameWhenCut(event);
                openList();
              }}
              onBlur={() => dropdown.close()}
            />
            {/* A search icon marks the field as one that searches, beside the
                chevron that opens it: the specification draws both. */}
            <span className={styles.searchGlyph} aria-hidden="true" data-dropdown-search="true">
              <Icon meaning="search" size="inline" />
            </span>
            <button
              type="button"
              className={`${fieldStyles.iconButton} ${styles.chevron}`}
              aria-label={t('dropdown.toggle')}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={onChevron}
              data-chevron="true"
            >
              <Icon meaning="expand" size="header" />
            </button>
          </div>
          {list && placement ? createPortal(list, placement.layer) : null}
        </div>
      )}
    </FieldShell>
  );
}
